import { useState, useEffect } from "react";
import { 
  Search, 
  Filter, 
  ChevronDown, 
  ChevronRight,
  User,
  Mail,
  Phone,
  Calendar,
  Trophy,
  MapPin,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Eye,
  MessageSquare,
  Plus,
  Trash2,
  ExternalLink,
  Users,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configuration
const STATUS_CONFIG = {
  neu: { 
    label: "Neu", 
    color: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    icon: AlertCircle,
    description: "Bewerbung eingegangen, noch nicht geprüft"
  },
  in_pruefung: { 
    label: "In Prüfung", 
    color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    icon: Clock,
    description: "Bewerbung wird aktuell geprüft"
  },
  sichtung_eingeladen: { 
    label: "Sichtung eingeladen", 
    color: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    icon: Calendar,
    description: "Spieler zum Sichtungstraining eingeladen"
  },
  sichtung_absolviert: { 
    label: "Sichtung absolviert", 
    color: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
    icon: CheckCircle2,
    description: "Sichtungstraining absolviert"
  },
  aufgenommen: { 
    label: "Aufgenommen", 
    color: "bg-green-500/20 text-green-400 border-green-500/30",
    icon: CheckCircle2,
    description: "In die Akademie aufgenommen"
  },
  warteliste: { 
    label: "Warteliste", 
    color: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    icon: Clock,
    description: "Auf der Warteliste"
  },
  abgelehnt: { 
    label: "Abgelehnt", 
    color: "bg-red-500/20 text-red-400 border-red-500/30",
    icon: XCircle,
    description: "Bewerbung abgelehnt"
  }
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.neu;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border rounded-full ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// Detail Modal Component
const BewerbungDetailModal = ({ bewerbung, onClose, onStatusChange, onAddNote, onDeleteNote, adminToken }) => {
  const [newNote, setNewNote] = useState("");
  const [newStatus, setNewStatus] = useState(bewerbung.status);
  const [statusComment, setStatusComment] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const handleStatusUpdate = async () => {
    if (newStatus === bewerbung.status) return;
    
    setIsUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/bewerbung/${bewerbung.bewerbung_id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Token": adminToken
        },
        body: JSON.stringify({
          status: newStatus,
          kommentar: statusComment
        })
      });

      if (!response.ok) throw new Error("Status-Update fehlgeschlagen");
      
      onStatusChange(bewerbung.bewerbung_id, newStatus);
      toast.success("Status aktualisiert");
      setStatusComment("");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      const response = await fetch(`${API_URL}/api/bewerbung/${bewerbung.bewerbung_id}/notiz`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Admin-Token": adminToken
        },
        body: JSON.stringify({
          text: newNote,
          autor: "Admin"
        })
      });

      if (!response.ok) throw new Error("Notiz konnte nicht hinzugefügt werden");
      
      const data = await response.json();
      onAddNote(bewerbung.bewerbung_id, data.notiz);
      setNewNote("");
      toast.success("Notiz hinzugefügt");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteNote = async (notizId) => {
    try {
      const response = await fetch(`${API_URL}/api/bewerbung/${bewerbung.bewerbung_id}/notiz/${notizId}`, {
        method: "DELETE",
        headers: {
          "X-Admin-Token": adminToken
        }
      });

      if (!response.ok) throw new Error("Notiz konnte nicht gelöscht werden");
      
      onDeleteNote(bewerbung.bewerbung_id, notizId);
      toast.success("Notiz gelöscht");
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#1A1A1A] border border-white/10 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#1A1A1A] border-b border-white/10 p-6 flex items-center justify-between">
          <div>
            <p className="text-[#bea777] text-sm font-mono">{bewerbung.bewerbung_id}</p>
            <h2 className="text-xl font-semibold text-white">
              {bewerbung.spieler.vorname} {bewerbung.spieler.nachname}
            </h2>
            {bewerbung.gewaehltesPaket && (
              <span className="inline-block mt-1 bg-[#bea777]/20 text-[#bea777] text-xs px-2 py-1 rounded">
                {bewerbung.gewaehltesPaket === "jahresvertrag" && "Jahresvertrag - 64,83 EUR/Monat"}
                {bewerbung.gewaehltesPaket === "halbjahresvertrag" && "Halbjahresvertrag - 79 EUR/Monat"}
                {bewerbung.gewaehltesPaket === "monatsvertrag" && "Monatsvertrag - 89 EUR/Monat"}
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-[#71717A] hover:text-white p-2">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status Section */}
          <div className="bg-[#121212] border border-white/5 p-4">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#bea777]" />
              Status verwalten
            </h3>
            <div className="flex flex-col sm:flex-row gap-4">
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="bg-[#1A1A1A] border-white/10 text-white w-full sm:w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="Kommentar (optional)"
                value={statusComment}
                onChange={(e) => setStatusComment(e.target.value)}
                className="bg-[#1A1A1A] border-white/10 text-white flex-1"
              />
              <Button
                onClick={handleStatusUpdate}
                disabled={newStatus === bewerbung.status || isUpdating}
                className="bg-[#bea777] hover:bg-[#d4c9a0] text-[#0A0A0A] rounded-none"
              >
                {isUpdating ? "Wird gespeichert..." : "Speichern"}
              </Button>
            </div>
          </div>

          {/* Player Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-[#121212] border border-white/5 p-4">
              <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                <User className="w-4 h-4 text-[#bea777]" />
                Spielerdaten
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#71717A]">Name</span>
                  <span className="text-white">{bewerbung.spieler.vorname} {bewerbung.spieler.nachname}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#71717A]">Geburtsdatum</span>
                  <span className="text-white">{bewerbung.spieler.geburtsdatum}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#71717A]">Jahrgang</span>
                  <span className="text-[#bea777] font-medium">{bewerbung.spieler.jahrgang}</span>
                </div>
                {bewerbung.spieler.groesse && (
                  <div className="flex justify-between">
                    <span className="text-[#71717A]">Größe</span>
                    <span className="text-white">{bewerbung.spieler.groesse} cm</span>
                  </div>
                )}
                {bewerbung.spieler.trikotgroesse && (
                  <div className="flex justify-between">
                    <span className="text-[#71717A]">Trikotgröße</span>
                    <span className="text-white">{bewerbung.spieler.trikotgroesse}</span>
                  </div>
                )}
                {bewerbung.spieler.hosengroesse && (
                  <div className="flex justify-between">
                    <span className="text-[#71717A]">Hosengröße</span>
                    <span className="text-white">{bewerbung.spieler.hosengroesse}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-[#71717A]">Position</span>
                  <span className="text-white">{bewerbung.spieler.position}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#71717A]">Spielfuß</span>
                  <span className="text-white">{bewerbung.spieler.spielfuss}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#71717A]">Aktueller Verein</span>
                  <span className="text-white">{bewerbung.spieler.aktuellerVerein || "-"}</span>
                </div>
                {bewerbung.spieler.trikotnummer && (
                  <div className="flex justify-between">
                    <span className="text-[#71717A]">Trikotnummer</span>
                    <span className="text-white">{bewerbung.spieler.trikotnummer}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-[#121212] border border-white/5 p-4">
              <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                <Users className="w-4 h-4 text-[#bea777]" />
                Kontaktdaten Eltern
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#71717A]">Name</span>
                  <span className="text-white">{bewerbung.eltern.vorname} {bewerbung.eltern.nachname}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#71717A]">Verhältnis</span>
                  <span className="text-white">{bewerbung.eltern.verhaeltnis}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#71717A]">E-Mail</span>
                  <a href={`mailto:${bewerbung.eltern.email}`} className="text-[#bea777] hover:underline flex items-center gap-1">
                    {bewerbung.eltern.email}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#71717A]">Telefon</span>
                  <a href={`tel:${bewerbung.eltern.telefon}`} className="text-[#bea777] hover:underline flex items-center gap-1">
                    {bewerbung.eltern.telefon}
                    <Phone className="w-3 h-3" />
                  </a>
                </div>
                {bewerbung.eltern.strasse && (
                  <div className="flex justify-between">
                    <span className="text-[#71717A]">Adresse</span>
                    <span className="text-white text-right">
                      {bewerbung.eltern.strasse} {bewerbung.eltern.hausnummer}<br />
                      {bewerbung.eltern.plz} {bewerbung.eltern.ort}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sportlicher Hintergrund */}
          <div className="bg-[#121212] border border-white/5 p-4">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <Trophy className="w-4 h-4 text-[#bea777]" />
              Sportlicher Hintergrund
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-[#71717A] mb-1">Trainingshäufigkeit</p>
                <p className="text-white">{bewerbung.sportlicherHintergrund?.trainingshaeufigkeit || "-"}</p>
              </div>
              <div>
                <p className="text-[#71717A] mb-1">Bisherige Vereine</p>
                <p className="text-white">{bewerbung.sportlicherHintergrund?.vereinshistorie || "-"}</p>
              </div>
              <div>
                <p className="text-[#71717A] mb-1">Stärken</p>
                <p className="text-white">{bewerbung.sportlicherHintergrund?.staerken || "-"}</p>
              </div>
              <div>
                <p className="text-[#71717A] mb-1">Entwicklungsfelder</p>
                <p className="text-white">{bewerbung.sportlicherHintergrund?.schwaechen || "-"}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-[#71717A] mb-1">Erfolge</p>
                <p className="text-white">{bewerbung.sportlicherHintergrund?.erfolge || "-"}</p>
              </div>
            </div>
          </div>

          {/* Motivation */}
          <div className="bg-[#121212] border border-white/5 p-4">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#bea777]" />
              Motivation
            </h3>
            <p className="text-[#A1A1AA] whitespace-pre-wrap">{bewerbung.motivation}</p>
            {bewerbung.videoUrl && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-[#71717A] text-sm mb-2">Video-Link</p>
                <a 
                  href={bewerbung.videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#bea777] hover:underline flex items-center gap-1"
                >
                  Video ansehen
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </div>

          {/* Notes Section */}
          <div className="bg-[#121212] border border-white/5 p-4">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-[#bea777]" />
              Interne Notizen
            </h3>
            
            {/* Add Note */}
            <div className="flex gap-2 mb-4">
              <Textarea
                placeholder="Neue Notiz hinzufügen..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="bg-[#1A1A1A] border-white/10 text-white min-h-[60px]"
              />
              <Button
                onClick={handleAddNote}
                disabled={!newNote.trim()}
                className="bg-[#bea777] hover:bg-[#d4c9a0] text-[#0A0A0A] rounded-none px-4"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Notes List */}
            <div className="space-y-3">
              {bewerbung.notizen && bewerbung.notizen.length > 0 ? (
                bewerbung.notizen.map((notiz) => (
                  <div key={notiz.id} className="bg-[#1A1A1A] border border-white/5 p-3 group">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-[#71717A] text-xs">
                        {notiz.autor} · {new Date(notiz.datum).toLocaleDateString('de-DE')}
                      </span>
                      <button
                        onClick={() => handleDeleteNote(notiz.id)}
                        className="text-[#71717A] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-white text-sm">{notiz.text}</p>
                  </div>
                ))
              ) : (
                <p className="text-[#71717A] text-sm text-center py-4">Keine Notizen vorhanden</p>
              )}
            </div>
          </div>

          {/* Status History */}
          <div className="bg-[#121212] border border-white/5 p-4">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#bea777]" />
              Status-Historie
            </h3>
            <div className="space-y-2">
              {bewerbung.statusHistorie && bewerbung.statusHistorie.length > 0 ? (
                bewerbung.statusHistorie.slice().reverse().map((entry, index) => (
                  <div key={index} className="flex items-start gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-[#bea777] mt-1.5" />
                    <div>
                      <p className="text-white">
                        <StatusBadge status={entry.status} />
                      </p>
                      <p className="text-[#71717A] text-xs mt-1">
                        {new Date(entry.datum).toLocaleDateString('de-DE', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        {entry.kommentar && ` · ${entry.kommentar}`}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-[#71717A] text-sm">Keine Historie vorhanden</p>
              )}
            </div>
          </div>

          {/* Meta Info */}
          <div className="text-center text-[#71717A] text-xs">
            Erstellt: {new Date(bewerbung.created_at).toLocaleDateString('de-DE')} · 
            Zuletzt aktualisiert: {new Date(bewerbung.updated_at).toLocaleDateString('de-DE')}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
export default function BewerbungenTab({ adminToken }) {
  const [bewerbungen, setBewerbungen] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterJahrgang, setFilterJahrgang] = useState("");
  const [selectedBewerbung, setSelectedBewerbung] = useState(null);
  
  // Admin Actions State
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceMonth, setInvoiceMonth] = useState(new Date().getMonth() + 1);
  const [invoiceYear, setInvoiceYear] = useState(new Date().getFullYear());
  const [isGeneratingInvoices, setIsGeneratingInvoices] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchBewerbungen = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (filterStatus) params.append("status", filterStatus);
      if (filterJahrgang) params.append("jahrgang", filterJahrgang);

      const response = await fetch(`${API_URL}/api/bewerbung/all?${params}`, {
        headers: {
          "X-Admin-Token": adminToken
        }
      });

      if (!response.ok) throw new Error("Fehler beim Laden der Bewerbungen");
      
      const data = await response.json();
      setBewerbungen(data.bewerbungen);
      setStats(data.stats);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (adminToken) {
      fetchBewerbungen();
    }
  }, [adminToken, searchQuery, filterStatus, filterJahrgang]);

  const handleStatusChange = (bewerbungId, newStatus) => {
    setBewerbungen(prev => 
      prev.map(b => 
        b.bewerbung_id === bewerbungId 
          ? { ...b, status: newStatus }
          : b
      )
    );
    if (selectedBewerbung?.bewerbung_id === bewerbungId) {
      setSelectedBewerbung(prev => ({ ...prev, status: newStatus }));
    }
    fetchBewerbungen(); // Refresh to update stats
  };

  const handleAddNote = (bewerbungId, notiz) => {
    setBewerbungen(prev =>
      prev.map(b =>
        b.bewerbung_id === bewerbungId
          ? { ...b, notizen: [...(b.notizen || []), notiz] }
          : b
      )
    );
    if (selectedBewerbung?.bewerbung_id === bewerbungId) {
      setSelectedBewerbung(prev => ({
        ...prev,
        notizen: [...(prev.notizen || []), notiz]
      }));
    }
  };

  const handleDeleteNote = (bewerbungId, notizId) => {
    setBewerbungen(prev =>
      prev.map(b =>
        b.bewerbung_id === bewerbungId
          ? { ...b, notizen: (b.notizen || []).filter(n => n.id !== notizId) }
          : b
      )
    );
    if (selectedBewerbung?.bewerbung_id === bewerbungId) {
      setSelectedBewerbung(prev => ({
        ...prev,
        notizen: (prev.notizen || []).filter(n => n.id !== notizId)
      }));
    }
  };

  // Delete Player
  const handleDeletePlayer = async (bewerbung, deleteInvoices = false) => {
    try {
      const response = await fetch(
        `${API_URL}/api/bewerbung/admin/${bewerbung.bewerbung_id}?delete_invoices=${deleteInvoices}`,
        {
          method: "DELETE",
          headers: {
            "X-Admin-Token": adminToken
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Fehler beim Löschen");
      }

      const result = await response.json();
      toast.success(result.message);
      setDeleteConfirm(null);
      fetchBewerbungen();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Generate Monthly Invoices Preview
  const handleInvoicePreview = async () => {
    setIsGeneratingInvoices(true);
    try {
      const response = await fetch(
        `${API_URL}/api/invoices/admin/akademie/generate-monthly-invoices?month=${invoiceMonth}&year=${invoiceYear}&dry_run=true&send_emails=false`,
        {
          method: "POST",
          headers: {
            "X-Admin-Token": adminToken
          }
        }
      );

      if (!response.ok) throw new Error("Fehler bei der Vorschau");

      const result = await response.json();
      setInvoicePreview(result.results);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsGeneratingInvoices(false);
    }
  };

  // Generate and Send Monthly Invoices
  const handleGenerateInvoices = async () => {
    if (!confirm(`Wirklich Rechnungen für ${getMonthName(invoiceMonth)} ${invoiceYear} erstellen und versenden?`)) {
      return;
    }

    setIsGeneratingInvoices(true);
    try {
      const response = await fetch(
        `${API_URL}/api/invoices/admin/akademie/generate-monthly-invoices?month=${invoiceMonth}&year=${invoiceYear}&dry_run=false&send_emails=true`,
        {
          method: "POST",
          headers: {
            "X-Admin-Token": adminToken
          }
        }
      );

      if (!response.ok) throw new Error("Fehler beim Erstellen der Rechnungen");

      const result = await response.json();
      toast.success(`${result.results.invoices_created.length} Rechnungen erstellt und versendet!`);
      setShowInvoiceModal(false);
      setInvoicePreview(null);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsGeneratingInvoices(false);
    }
  };

  const getMonthName = (month) => {
    const months = ["Januar", "Februar", "März", "April", "Mai", "Juni", 
                    "Juli", "August", "September", "Oktober", "November", "Dezember"];
    return months[month - 1];
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-[#1A1A1A] border border-white/5 p-4">
            <p className="text-[#71717A] text-sm">Gesamt</p>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="bg-blue-500/10 border border-blue-500/20 p-4">
            <p className="text-blue-400 text-sm">Neu</p>
            <p className="text-2xl font-bold text-blue-400">{stats.neu}</p>
          </div>
          <div className="bg-yellow-500/10 border border-yellow-500/20 p-4">
            <p className="text-yellow-400 text-sm">In Prüfung</p>
            <p className="text-2xl font-bold text-yellow-400">{stats.in_pruefung}</p>
          </div>
          <div className="bg-purple-500/10 border border-purple-500/20 p-4">
            <p className="text-purple-400 text-sm">Sichtung</p>
            <p className="text-2xl font-bold text-purple-400">{stats.sichtung_eingeladen}</p>
          </div>
          <div className="bg-green-500/10 border border-green-500/20 p-4">
            <p className="text-green-400 text-sm">Aufgenommen</p>
            <p className="text-2xl font-bold text-green-400">{stats.aufgenommen}</p>
          </div>
        </div>
      )}

      {/* Admin Actions */}
      <div className="bg-[#1A1A1A] border border-white/5 p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-white font-medium flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#bea777]" />
              Akademie-Rechnungen
            </h3>
            <p className="text-[#71717A] text-sm mt-1">
              Monatsrechnungen für alle aufgenommenen Spieler erstellen
            </p>
          </div>
          <Button
            onClick={() => setShowInvoiceModal(true)}
            className="bg-[#bea777] hover:bg-[#d4c9a0] text-[#0A0A0A] rounded-none"
          >
            <FileText className="w-4 h-4 mr-2" />
            Monatsrechnungen erstellen
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
          <Input
            placeholder="Suche nach Name, E-Mail, Verein..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-[#1A1A1A] border-white/10 text-white pl-10"
          />
        </div>
        <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="bg-[#1A1A1A] border-white/10 text-white w-full sm:w-48">
            <SelectValue placeholder="Alle Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-white/10">
            <SelectItem value="all">Alle Status</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterJahrgang || "all"} onValueChange={(v) => setFilterJahrgang(v === "all" ? "" : v)}>
          <SelectTrigger className="bg-[#1A1A1A] border-white/10 text-white w-full sm:w-36">
            <SelectValue placeholder="Jahrgang" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-white/10">
            <SelectItem value="all">Alle</SelectItem>
            <SelectItem value="U8">U8</SelectItem>
            <SelectItem value="U9">U9</SelectItem>
            <SelectItem value="U10">U10</SelectItem>
            <SelectItem value="U11">U11</SelectItem>
            <SelectItem value="U12">U12</SelectItem>
            <SelectItem value="U13">U13</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Applications List */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-2 border-[#bea777] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#71717A]">Lade Bewerbungen...</p>
        </div>
      ) : bewerbungen.length === 0 ? (
        <div className="text-center py-12 bg-[#1A1A1A] border border-white/5">
          <FileText className="w-12 h-12 text-[#71717A] mx-auto mb-4" />
          <p className="text-white mb-2">Keine Bewerbungen gefunden</p>
          <p className="text-[#71717A] text-sm">
            {searchQuery || filterStatus || filterJahrgang 
              ? "Versuche andere Filteroptionen" 
              : "Neue Bewerbungen werden hier angezeigt"}
          </p>
        </div>
      ) : (
        <div className="bg-[#1A1A1A] border border-white/5 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-[#71717A] text-xs font-medium uppercase">Spieler</th>
                  <th className="text-left p-4 text-[#71717A] text-xs font-medium uppercase">Jahrgang</th>
                  <th className="text-left p-4 text-[#71717A] text-xs font-medium uppercase">Position</th>
                  <th className="text-left p-4 text-[#71717A] text-xs font-medium uppercase">Verein</th>
                  <th className="text-left p-4 text-[#71717A] text-xs font-medium uppercase">Status</th>
                  <th className="text-left p-4 text-[#71717A] text-xs font-medium uppercase">Datum</th>
                  <th className="text-right p-4 text-[#71717A] text-xs font-medium uppercase">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {bewerbungen.map((bewerbung) => (
                  <tr 
                    key={bewerbung.bewerbung_id} 
                    className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                    onClick={() => setSelectedBewerbung(bewerbung)}
                  >
                    <td className="p-4">
                      <div>
                        <p className="text-white font-medium">
                          {bewerbung.spieler.vorname} {bewerbung.spieler.nachname}
                        </p>
                        <p className="text-[#71717A] text-xs font-mono">{bewerbung.bewerbung_id}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-[#bea777] font-medium">{bewerbung.spieler.jahrgang}</span>
                    </td>
                    <td className="p-4 text-[#A1A1AA]">{bewerbung.spieler.position}</td>
                    <td className="p-4 text-[#A1A1AA]">{bewerbung.spieler.aktuellerVerein || "-"}</td>
                    <td className="p-4">
                      <StatusBadge status={bewerbung.status} />
                    </td>
                    <td className="p-4 text-[#71717A] text-sm">
                      {new Date(bewerbung.created_at).toLocaleDateString('de-DE')}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedBewerbung(bewerbung);
                          }}
                          className="text-[#bea777] hover:text-[#d4c9a0] hover:bg-[#bea777]/10"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirm(bewerbung);
                          }}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedBewerbung && (
        <BewerbungDetailModal
          bewerbung={selectedBewerbung}
          onClose={() => setSelectedBewerbung(null)}
          onStatusChange={handleStatusChange}
          onAddNote={handleAddNote}
          onDeleteNote={handleDeleteNote}
          adminToken={adminToken}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0A0A0A] border border-white/10 max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-500/20 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-white font-medium">Spieler löschen?</h3>
                <p className="text-[#71717A] text-sm">Diese Aktion kann nicht rückgängig gemacht werden</p>
              </div>
            </div>
            
            <div className="bg-[#1A1A1A] border border-white/5 p-4 mb-4">
              <p className="text-white font-medium">
                {deleteConfirm.spieler?.vorname} {deleteConfirm.spieler?.nachname}
              </p>
              <p className="text-[#71717A] text-sm">
                {deleteConfirm.spieler?.jahrgang} · {deleteConfirm.bewerbung_id}
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setDeleteConfirm(null)}
                variant="outline"
                className="flex-1 border-white/10 text-white hover:bg-white/5"
              >
                Abbrechen
              </Button>
              <Button
                onClick={() => handleDeletePlayer(deleteConfirm, false)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Löschen
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Generation Modal */}
      {showInvoiceModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0A0A0A] border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#0A0A0A] border-b border-white/10 p-4 flex items-center justify-between">
              <h3 className="text-white font-medium flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#bea777]" />
                Monatsrechnungen erstellen
              </h3>
              <button
                onClick={() => {
                  setShowInvoiceModal(false);
                  setInvoicePreview(null);
                }}
                className="text-[#71717A] hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Month/Year Selection */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <Label className="text-white mb-2 block">Monat</Label>
                  <Select value={invoiceMonth.toString()} onValueChange={(v) => setInvoiceMonth(parseInt(v))}>
                    <SelectTrigger className="bg-[#1A1A1A] border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">
                      {["Januar", "Februar", "März", "April", "Mai", "Juni", 
                        "Juli", "August", "September", "Oktober", "November", "Dezember"].map((m, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label className="text-white mb-2 block">Jahr</Label>
                  <Select value={invoiceYear.toString()} onValueChange={(v) => setInvoiceYear(parseInt(v))}>
                    <SelectTrigger className="bg-[#1A1A1A] border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">
                      <SelectItem value="2025">2025</SelectItem>
                      <SelectItem value="2026">2026</SelectItem>
                      <SelectItem value="2027">2027</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Preview Button */}
              <Button
                onClick={handleInvoicePreview}
                disabled={isGeneratingInvoices}
                className="w-full bg-[#1A1A1A] border border-white/10 text-white hover:bg-white/5"
              >
                {isGeneratingInvoices ? "Lade Vorschau..." : "Vorschau anzeigen"}
              </Button>

              {/* Preview Results */}
              {invoicePreview && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="bg-green-500/10 border border-green-500/20 p-3">
                      <p className="text-green-400 text-2xl font-bold">{invoicePreview.invoices_created?.length || 0}</p>
                      <p className="text-green-400 text-xs">Zu erstellen</p>
                    </div>
                    <div className="bg-yellow-500/10 border border-yellow-500/20 p-3">
                      <p className="text-yellow-400 text-2xl font-bold">{invoicePreview.skipped_duplicate?.length || 0}</p>
                      <p className="text-yellow-400 text-xs">Bereits vorhanden</p>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/20 p-3">
                      <p className="text-red-400 text-2xl font-bold">{invoicePreview.skipped_missing_data?.length || 0}</p>
                      <p className="text-red-400 text-xs">Fehlende Daten</p>
                    </div>
                  </div>

                  {/* List of invoices to create */}
                  {invoicePreview.invoices_created?.length > 0 && (
                    <div className="bg-[#1A1A1A] border border-white/5 p-4">
                      <h4 className="text-white font-medium mb-3">Rechnungen werden erstellt für:</h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {invoicePreview.invoices_created.map((inv, i) => (
                          <div key={i} className="flex justify-between items-center text-sm py-2 border-b border-white/5 last:border-0">
                            <div>
                              <p className="text-white">{inv.spieler}</p>
                              <p className="text-[#71717A] text-xs">{inv.empfaenger} · {inv.email}</p>
                            </div>
                            <span className="text-[#bea777] font-medium">{inv.betrag}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Duplicates */}
                  {invoicePreview.skipped_duplicate?.length > 0 && (
                    <div className="bg-yellow-500/10 border border-yellow-500/20 p-4">
                      <h4 className="text-yellow-400 font-medium mb-2">Bereits vorhanden:</h4>
                      <div className="space-y-1 text-sm">
                        {invoicePreview.skipped_duplicate.map((skip, i) => (
                          <p key={i} className="text-yellow-400/80">
                            {skip.spieler} - {skip.existing_invoice}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Generate Button */}
                  {invoicePreview.invoices_created?.length > 0 && (
                    <Button
                      onClick={handleGenerateInvoices}
                      disabled={isGeneratingInvoices}
                      className="w-full bg-[#bea777] hover:bg-[#d4c9a0] text-[#0A0A0A]"
                    >
                      {isGeneratingInvoices 
                        ? "Erstelle Rechnungen..." 
                        : `${invoicePreview.invoices_created.length} Rechnungen erstellen & versenden`}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
